import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TranslationNS } from '@gokrew/models/constants';
import { KollabHRFullLogo, BrandPanelIllustration } from '@gokrew/ui/assets';
import { BrandUnderline } from '@gokrew/ui/components';
import { getConfig } from '@gokrew/ui/runtime-config';
import styles from './BrandPanel.module.css';

interface BrandPanelProps {
  footer?: React.ReactNode;
}

export function BrandPanel({ footer }: BrandPanelProps) {
  const { t } = useTranslation(TranslationNS.Auth);
  const employeePortalUrl = getConfig().EMPLOYEE_PORTAL_URL;

  return (
    <div className={styles.panel}>
      {/* Yellow circle sweep — animates from bottom-center outward */}
      <div className={styles.bgSweep} aria-hidden="true" />

      {/* Logo */}
      <Link to="/login" className={styles.logoLink}>
        <img src={KollabHRFullLogo} alt="KollabHR" className={styles.logo} />
      </Link>

      {/* Main body — headline, subtext, portal block */}
      <div className={styles.body}>
        <h1 className={styles.headline}>
          <span>{t('signup_v2.brand.headline_line1')}</span>
          <br />
          <span>{t('signup_v2.brand.headline_line2')}</span>
          <br />
          {/* "workforce." line gets the animated curved underline */}
          <span className={styles.headlineUnderlined}>
            {t('signup_v2.brand.headline_line3')}
            <BrandUnderline />
          </span>
        </h1>

        <p className={styles.tagline}>{t('signup_v2.brand.tagline')}</p>

        {/* Employee Portal block */}
        <div className={styles.portal}>
          <p className={styles.portalLabel}>{t('employee_portal')}</p>
          <p className={styles.portalText}>{t('employee_portal_description')}</p>
          <button
            type="button"
            className={styles.portalBtn}
            onClick={() =>
              employeePortalUrl &&
              window.open(employeePortalUrl, '_blank', 'noopener,noreferrer')
            }
          >
            {t('access_employee_portal')}
          </button>
        </div>
      </div>

      {/* Illustration — sits flush at the bottom */}
      <div className={styles.illustrationWrap} aria-hidden="true">
        <img
          src={BrandPanelIllustration}
          alt=""
          className={styles.illustration}
        />
      </div>

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
